import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Loadable from 'react-loadable';

import { Fieldset } from 'components/form/fieldset/fieldset';
import { Colors } from 'components/utils/styles/ui';

import * as styled from './styles/fieldset';
import i18n from './utils/i18n';

const MIN_PASSWORD_LENGTH = 8;

const PasswordStrengthBar = Loadable({
  loader: () => import('react-password-strength-bar'),
  loading: () => null,
  render(loaded, props) {
    const { default: PasswordStrengthBarComponent } = loaded;
    return <PasswordStrengthBarComponent {...props} />;
  },
});

export const PasswordFieldsetComponent = function PasswordFieldsetComponent(
  props
) {
  const { onStrengthChange, isValid, intl, ...otherProps } = props;
  const [passwordScore, setPasswordScore] = useState({
    password: null,
    valid: false,
  });
  const onStrengthChangeRef = useRef(onStrengthChange);
  const password = otherProps.inputValue || '';
  const passwordStrong =
    passwordScore.password === password
      ? passwordScore.valid
      : password.length >= MIN_PASSWORD_LENGTH;

  useEffect(() => {
    onStrengthChangeRef.current = onStrengthChange;
  }, [onStrengthChange]);

  useEffect(() => {
    onStrengthChangeRef.current(passwordStrong);
  }, [passwordStrong]);

  const handlePasswordStrengthChange = (score) => {
    const valid = !(score === 0 || score === 1);
    setPasswordScore({ password, valid });
  };

  const PasswordStrength = (
    <styled.PasswordStrengthWrapper>
      <PasswordStrengthBar
        password={password}
        barColors={[
          Colors.grey,
          Colors.red,
          Colors.yellowDark,
          Colors.tealDark,
          Colors.green,
        ]}
        scoreWords={[
          intl.formatMessage(i18n.passwordTooWeak),
          intl.formatMessage(i18n.passwordTooWeak),
          intl.formatMessage(i18n.passwordOkay),
          intl.formatMessage(i18n.passwordGood),
          intl.formatMessage(i18n.passwordStrong),
        ]}
        onChangeScore={handlePasswordStrengthChange}
        minLength={MIN_PASSWORD_LENGTH}
      />
    </styled.PasswordStrengthWrapper>
  );

  return (
    <Fieldset
      bottomChildren={PasswordStrength}
      isValid={otherProps.shouldValidate ? passwordStrong && isValid : isValid}
      {...otherProps}
    />
  );
};

export const PasswordFieldset = injectIntl(PasswordFieldsetComponent);

PasswordFieldsetComponent.propTypes = {
  ...Fieldset.propTypes,
  onStrengthChange: PropTypes.func.isRequired,
  isValid: PropTypes.bool,
  inputValue: PropTypes.string,
};
